var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { ErrorType } from "../types/ErrorType";
import { getAssignBlockedReasonMessageId, NEW_PREFIX_BY_PUBLISHER_ONLY_MESSAGE_ID, NO_AGENCY_PREFIX_MESSAGE_ID, NO_PREFIX_RANGE_MESSAGE_ID, NUMBER_ASSIGNMENT_IN_PROGRESS_MESSAGE_ID, NUMBERS_RESERVED_MESSAGE_ID, AGENCY_PREFIX_CHOICE_REQUIRED_MESSAGE_ID, CHOSEN_PREFIX_NO_FREE_NUMBER_MESSAGE_ID, PREFIX_NO_FREE_NUMBER_MESSAGE_ID, PUBLISHER_CLOSING_MESSAGE_ID, } from "./productNumberAssignErrors";
import { PublisherPrefixStatusEnum, } from "../types/Api";
var NO_NUMBERS_MESSAGE_ID = "ui-br-agency.title.products.panel.assign.noNumbers";
/** Highest block size (magnitude) of a new publisher prefix ever offered: 1000 numbers. */
export var MAX_NEW_PREFIX_MAGNITUDE = 3;
/** Smallest block size (magnitude) of a new publisher prefix: 10 numbers. */
export var MIN_NEW_PREFIX_MAGNITUDE = 1;
var OTHER_MAGNITUDE_LABEL_ID = "ui-br-agency.product-number.assign.magnitude.option.other";
/** Block-size options for creating a new publisher prefix, smallest first. */
export var NEW_PREFIX_MAGNITUDE_OPTIONS = [
    {
        value: 1,
        labelId: "ui-br-agency.product-number.assign.magnitude.option.10",
    },
    {
        value: 2,
        labelId: "ui-br-agency.product-number.assign.magnitude.option.100",
    },
    {
        value: 3,
        labelId: "ui-br-agency.product-number.assign.magnitude.option.1000",
    },
];
/** Message shown when the ranges of the picked block size ran out before the assignment (Tools #76358). */
export var PREFIX_SIZE_NOT_AVAILABLE_MESSAGE_ID = "ui-br-agency.title.products.panel.assign.reason.prefixSizeNotAvailable";
/** Message shown when the backend rejects a too large new prefix (FSD PP20). */
export var MAGNITUDE_NOT_ALLOWED_MESSAGE_ID = "ui-br-agency.product-number.assign.error.magnitudeNotAllowed";
/**
 * Highest block size the user may pick, from the backend {@code maxMagnitude} (FSD PP20: by the publisher's
 * production in the last two years; always 3 for agency workers). A missing value (older backend) keeps the
 * previous behaviour of offering everything up to 1000.
 */
export var resolveAllowedMaxMagnitude = function (maxMagnitude) {
    if (typeof maxMagnitude !== "number" || Number.isNaN(maxMagnitude)) {
        return MAX_NEW_PREFIX_MAGNITUDE;
    }
    return Math.min(MAX_NEW_PREFIX_MAGNITUDE, Math.max(MIN_NEW_PREFIX_MAGNITUDE, Math.floor(maxMagnitude)));
};
var toMagnitudeOption = function (magnitude) {
    var _a;
    return (_a = NEW_PREFIX_MAGNITUDE_OPTIONS.find(function (opt) { return opt.value === magnitude; })) !== null && _a !== void 0 ? _a : {
        value: magnitude,
        labelId: OTHER_MAGNITUDE_LABEL_ID,
        labelValues: { count: Math.pow(10, magnitude) },
    };
};
/**
 * Block-size options the user may pick, smallest first. The backend lists the sizes the agency can still allocate
 * ({@code allowedMagnitudes}, already capped by PP20; a collective publisher may get blocks larger than 1000). A
 * missing or empty list (older backend) falls back to every size up to {@code maxMagnitude}.
 */
export var getAllowedMagnitudeOptions = function (maxMagnitude, allowedMagnitudes) {
    if (allowedMagnitudes && allowedMagnitudes.length > 0) {
        return __spreadArray([], allowedMagnitudes, true).sort(function (first, second) { return first - second; })
            .map(toMagnitudeOption);
    }
    var allowed = resolveAllowedMaxMagnitude(maxMagnitude);
    return NEW_PREFIX_MAGNITUDE_OPTIONS.filter(function (opt) { return opt.value <= allowed; });
};
/**
 * Whether the assign page has to create a new publisher prefix for the selected agency prefix — also when the
 * publisher's prefixes there are all used up (Tools #76358). The backend preview for that selection decides; while
 * it is missing (loading, failed, or not asked) the page falls back to "the publisher has no prefix there".
 */
export var isNewPrefixNeeded = function (_a) {
    var selectionPreview = _a.selectionPreview, publisherPrefixCount = _a.publisherPrefixCount;
    return selectionPreview
        ? !!selectionPreview.createNewPublisherPrefix
        : publisherPrefixCount === 0;
};
/**
 * The publisher's prefixes a number can be drawn from now: those the backend decision for the selection lists as
 * usable (ACTIVE with a free number). Without that answer only ACTIVE prefixes are kept — a CLOSED one is rejected
 * by the backend anyway.
 */
export var filterUsablePublisherPrefixes = function (publisherPrefixes, selectionPreview) {
    var _a;
    if (selectionPreview) {
        var usableIds_1 = (_a = selectionPreview.usablePublisherPrefixIds) !== null && _a !== void 0 ? _a : [];
        return publisherPrefixes.filter(function (prefix) { return !!prefix.id && usableIds_1.includes(prefix.id); });
    }
    return publisherPrefixes.filter(function (prefix) { return prefix.status === PublisherPrefixStatusEnum.ACTIVE; });
};
/**
 * The notice the assign page shows (and submitting is blocked) when the backend decision for the selected agency
 * prefix yields no number: the blocking reason (only the publisher creates its new prefix, numbers reserved by another
 * assignment, publisher in liquidation, no free prefix range, ...). The combined "no numbers" text is only the fallback
 * of a backend that answers without a reason.
 */
export var resolveSelectionNoticeMessageId = function (selectionPreview) {
    var _a, _b;
    if (!selectionPreview) {
        return null;
    }
    var blockedMessageId = getAssignBlockedReasonMessageId((_a = selectionPreview.assignBlockedReason) !== null && _a !== void 0 ? _a : selectionPreview.noNumberReason);
    if (blockedMessageId) {
        return blockedMessageId;
    }
    if (!selectionPreview.createNewPublisherPrefix &&
        ((_b = selectionPreview.usablePublisherPrefixIds) !== null && _b !== void 0 ? _b : []).length === 0) {
        return NO_NUMBERS_MESSAGE_ID;
    }
    return null;
};
/**
 * The notice of the assign page from its first preview (RCK-1667): why the original selection - the title owner and
 * the agency prefix picked for it - yields no number. It describes only that selection, so it is shown only while
 * the page still has exactly that selection and is not loading a new one; any other selection gets its own decision.
 */
export var resolvePreviewNoNumberNoticeMessageId = function (_a) {
    var _b;
    var preview = _a.preview, selectedPublisherId = _a.selectedPublisherId, selectedAgencyPrefixId = _a.selectedAgencyPrefixId, loading = _a.loading;
    if (loading || !(preview === null || preview === void 0 ? void 0 : preview.noNumberReason)) {
        return null;
    }
    var describesSelection = (selectedPublisherId || null) === (preview.publisherId || null) &&
        (selectedAgencyPrefixId || null) === (((_b = preview.agencyPrefix) === null || _b === void 0 ? void 0 : _b.id) || null);
    return describesSelection
        ? getAssignBlockedReasonMessageId(preview.noNumberReason)
        : null;
};
/**
 * Texts of the new-prefix size choice: the publisher (or, in a co-edition, the co-publisher) either has no prefix yet,
 * or its active prefix is used up and a new one replaces it (Tools #76358).
 */
export var resolveNewPrefixChoiceMessageIds = function (_a) {
    var _b = _a.replacesUsedUpPrefix, replacesUsedUpPrefix = _b === void 0 ? false : _b, _c = _a.forCoPublisher, forCoPublisher = _c === void 0 ? false : _c;
    var base = "ui-br-agency.title.products.panel.assign.newPrefix".concat(forCoPublisher ? ".coPublisher" : "").concat(replacesUsedUpPrefix ? ".usedUp" : "");
    return { legendId: base, onlyTenId: "".concat(base, ".onlyTen") };
};
/** Whether the only size on offer is the smallest one (10), so there is nothing to choose. */
export var offersOnlySmallestMagnitude = function (options) {
    return options.length === 1 && options[0].value === MIN_NEW_PREFIX_MAGNITUDE;
};
/**
 * The block size to show as selected and to submit: the user's choice while it is on offer, otherwise
 * (nothing chosen yet, or the choice is no longer allowed) the smallest size on offer.
 */
export var resolveEffectiveMagnitude = function (selected, maxMagnitude, allowedMagnitudes) {
    var _a, _b;
    var options = getAllowedMagnitudeOptions(maxMagnitude, allowedMagnitudes);
    if (selected !== null && options.some(function (opt) { return opt.value === selected; })) {
        return selected;
    }
    return (_b = (_a = options[0]) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : MIN_NEW_PREFIX_MAGNITUDE;
};
/** RCK-1667: the 422 error types of an assignment that got no number, each with its own text. */
var NO_NUMBER_ERROR_MESSAGE_IDS = (_a = {},
    _a[ErrorType.PRODUCT_NUMBERS_RESERVED] = NUMBERS_RESERVED_MESSAGE_ID,
    _a[ErrorType.PUBLISHER_CLOSING_NO_FREE_NUMBER] = PUBLISHER_CLOSING_MESSAGE_ID,
    _a[ErrorType.CANNOT_CREATE_PREFIX_FOR_CLOSING_PUBLISHER] = PUBLISHER_CLOSING_MESSAGE_ID,
    _a[ErrorType.NO_FREE_PUBLISHER_PREFIX_RANGE] = NO_PREFIX_RANGE_MESSAGE_ID,
    _a[ErrorType.PUBLISHER_PREFIX_NO_FREE_NUMBER] = PREFIX_NO_FREE_NUMBER_MESSAGE_ID,
    _a[ErrorType.NO_AGENCY_PREFIX] = NO_AGENCY_PREFIX_MESSAGE_ID,
    _a[ErrorType.PRODUCT_NUMBER_ASSIGNMENT_IN_PROGRESS] = NUMBER_ASSIGNMENT_IN_PROGRESS_MESSAGE_ID,
    _a[ErrorType.AGENCY_PREFIX_CHOICE_REQUIRED] = AGENCY_PREFIX_CHOICE_REQUIRED_MESSAGE_ID,
    _a[ErrorType.CHOSEN_PUBLISHER_PREFIX_NO_FREE_NUMBER] = CHOSEN_PREFIX_NO_FREE_NUMBER_MESSAGE_ID,
    _a);
/**
 * Message id for a failed assignment: the PP20 rejection, the publisher-only new prefix, a size whose ranges ran out
 * and every reason why no number could be drawn get their own text, anything else the fallback. A size nobody picked
 * ({@code automaticPrefixSize}, the collective prefix) whose ranges ran out means "no free prefix range", not "pick
 * another size".
 */
export var resolveAssignErrorMessageId = function (errorTypeCode, fallbackMessageId, automaticPrefixSize) {
    if (automaticPrefixSize === void 0) { automaticPrefixSize = false; }
    if (errorTypeCode === ErrorType.PUBLISHER_PREFIX_MAGNITUDE_NOT_ALLOWED) {
        return MAGNITUDE_NOT_ALLOWED_MESSAGE_ID;
    }
    if (errorTypeCode === ErrorType.NEW_PREFIX_BY_PUBLISHER_ONLY) {
        return NEW_PREFIX_BY_PUBLISHER_ONLY_MESSAGE_ID;
    }
    if (errorTypeCode === ErrorType.NO_ACTIVE_PUBLISHER_PREFIX_RANGES ||
        errorTypeCode === ErrorType.NO_AVAILABLE_PUBLISHER_PREFIX_VALUES) {
        return automaticPrefixSize
            ? NO_PREFIX_RANGE_MESSAGE_ID
            : PREFIX_SIZE_NOT_AVAILABLE_MESSAGE_ID;
    }
    if (errorTypeCode && NO_NUMBER_ERROR_MESSAGE_IDS[errorTypeCode]) {
        return NO_NUMBER_ERROR_MESSAGE_IDS[errorTypeCode];
    }
    return fallbackMessageId;
};
/**
 * Highest block size for the agency prefix currently selected on the assign page, from the backend preview made
 * for exactly that selection. While that value is unknown (loading, failed, or no new prefix would be created for
 * this selection) a publisher user is offered only the smallest block — the backend always accepts it — and never
 * a value left over from a previous selection; agency users keep every size up to 1000.
 */
export var resolveSelectionMaxMagnitude = function (_a) {
    var selectionPreview = _a.selectionPreview, isPublisherRoleOnly = _a.isPublisherRoleOnly;
    if ((selectionPreview === null || selectionPreview === void 0 ? void 0 : selectionPreview.createNewPublisherPrefix) &&
        typeof selectionPreview.maxMagnitude === "number") {
        return resolveAllowedMaxMagnitude(selectionPreview.maxMagnitude);
    }
    return isPublisherRoleOnly
        ? MIN_NEW_PREFIX_MAGNITUDE
        : MAX_NEW_PREFIX_MAGNITUDE;
};
